// src/screens/SetLocation.tsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import Navbar from "../components/Navbar";
import PageTitle from "../components/PageTitle";
import { PrimaryButton } from "../components/Button";

type LatLng = { lat: number; lng: number };

const DEFAULT_CENTER: LatLng = { lat: 1.3521, lng: 103.8198 }; // SG

const pinSvg = (fill = "#D62422") =>
  `data:image/svg+xml;utf8,` +
  encodeURIComponent(
    `<svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" fill="${fill}"/>
      <circle cx="12" cy="9" r="3" fill="white"/>
    </svg>`
  );

const googleMapsApiKey =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCOvcTx_ZE968LgZYsfFh8jAVNUR-THpkA";

const SetLocation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [center, setCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [selectedLocation, setSelectedLocation] = useState<LatLng | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [addressData, setAddressData] = useState<any>(null);
  const [geocodedAddress, setGeocodedAddress] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(true);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapsError("Google Maps API key is missing. Configure REACT_APP_GOOGLE_MAPS_API_KEY.");
      setMapsLoaded(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Listen for Google Maps errors and detect billing issues
  useEffect(() => {
    const handleMapError = (event: ErrorEvent) => {
      const errorMsg = event.message || event.error?.message || '';
      if (errorMsg.includes('BillingNotEnabledMapError') ||
        errorMsg.includes('billing') ||
        errorMsg.includes('Google Maps JavaScript API error')) {
        console.warn('Google Maps billing error detected:', errorMsg);
        setMapsError('Map unavailable due to billing configuration');
        setMapsLoaded(false);
        event.preventDefault(); // Prevent error from propagating
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorMsg = event.reason?.message || String(event.reason) || '';
      if (errorMsg.includes('BillingNotEnabledMapError') ||
        errorMsg.includes('billing') ||
        errorMsg.includes('Google Maps')) {
        console.warn('Google Maps billing error in promise rejection:', errorMsg);
        setMapsError('Map unavailable due to billing configuration');
        setMapsLoaded(false);
        event.preventDefault(); // Prevent error from propagating
      }
    };

    // Set a timeout to detect if map fails to load
    const loadTimeout = setTimeout(() => {
      if (!mapsLoaded && !mapsError) {
        // Check if there are any Google Maps errors in console
        // If maps API loaded but map didn't initialize, likely a billing issue
        if (typeof window !== 'undefined' && window.google?.maps) {
          // API loaded but map might have failed
          console.warn('Map did not initialize within timeout');
        } else {
          setMapsError('Map failed to load. You can still proceed with adding an address.');
          setMapsLoaded(false);
        }
      }
    }, 10000); // 10 second timeout

    window.addEventListener('error', handleMapError, true); // Use capture phase
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      clearTimeout(loadTimeout);
      window.removeEventListener('error', handleMapError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [mapsLoaded, mapsError]);

  const mapContainerStyle = useMemo(
    () => ({ width: "100%", height: "200px" }), // compact like mock
    []
  );

  // Handle edit mode initialization
  useEffect(() => {
    if (location.state?.editMode && location.state?.addressData) {
      setIsEditMode(true);
      setAddressData(location.state.addressData);
      // If address has coordinates, use them to set center and selected location
      const addr = location.state.addressData;
      if (addr.coordinates?.lat && addr.coordinates?.lng) {
        const coords = { lat: addr.coordinates.lat, lng: addr.coordinates.lng };
        setCenter(coords);
        setSelectedLocation(coords);
      }
    }
  }, [location.state]);

  // Reverse geocode coordinates to get address details
  const reverseGeocode = async (pos: LatLng) => {
    try {
      // Check if Google Maps API is fully loaded
      if (typeof window === 'undefined' || !window.google || !window.google.maps || !window.google.maps.Geocoder) {
        console.warn('Google Maps Geocoder not available');
        return null;
      }

      const geocoder = new window.google.maps.Geocoder();
      return new Promise<any>((resolve) => {
        try {
          geocoder.geocode({ location: pos }, (results, status) => {
            try {
              if (status === 'OK' && results && results[0]) {
                const addressComponents = results[0].address_components;
                const address: any = {};

                addressComponents.forEach((component: any) => {
                  const types = component.types;
                  if (types.includes('street_number') || types.includes('route')) {
                    // Street address is handled separately
                  } else if (types.includes('locality')) {
                    address.city = component.long_name;
                  } else if (types.includes('administrative_area_level_1')) {
                    address.state = component.long_name;
                  } else if (types.includes('postal_code')) {
                    address.postalCode = component.long_name;
                  } else if (types.includes('country')) {
                    address.country = component.long_name;
                  }
                });

                // Get formatted address
                address.formattedAddress = results[0].formatted_address;
                resolve(address);
              } else {
                resolve(null);
              }
            } catch (err) {
              console.error('Error processing geocode results:', err);
              resolve(null);
            }
          });
        } catch (err) {
          console.error('Error calling geocoder:', err);
          resolve(null);
        }
      });
    } catch (err) {
      console.error('Error in reverseGeocode:', err);
      return null;
    }
  };

  const parseAddressComponents = (place: google.maps.places.PlaceResult) => {
    const components = place.address_components || [];
    const address: any = {};
    components.forEach((component) => {
      const types = component.types || [];
      if (types.includes("locality")) {
        address.city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        address.state = component.long_name;
      }
      if (types.includes("postal_code")) {
        address.postalCode = component.long_name;
      }
      if (types.includes("country")) {
        address.country = component.long_name;
      }
    });
    address.formattedAddress = place.formatted_address || "";
    address.street = place.name || "";
    return address;
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const next = { lat, lng };
    setCenter(next);
    setSelectedLocation(next);
    setGeocodedAddress(parseAddressComponents(place));
    setSearchValue(place.formatted_address || place.name || "");
  };

  const onMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !isMounted || !mapsLoaded) return;
    const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setSelectedLocation(pos);
    setCenter(pos);

    // Reverse geocode to get address details
    try {
      const address = await reverseGeocode(pos);
      if (address && isMounted) {
        setGeocodedAddress(address);
      }
    } catch (err) {
      console.error('Error in onMapClick geocoding:', err);
    }
  };

  const useCurrentLocation = async () => {
    if (!navigator.geolocation || !isMounted || !mapsLoaded) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      if (!isMounted) return;
      const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setSelectedLocation(p);
      setCenter(p);

      // Reverse geocode to get address details
      try {
        const address = await reverseGeocode(p);
        if (address && isMounted) {
          setGeocodedAddress(address);
        }
      } catch (err) {
        console.error('Error in useCurrentLocation geocoding:', err);
      }
    }, (err) => {
      console.error('Error getting current location:', err);
    });
  };

  const addAddress = () => {
    // Use selected location if available, otherwise use current map center
    const locationToUse = selectedLocation || center;

    // Use geocoded address if available, otherwise fall back to existing address data or empty
    const addressDetails = geocodedAddress || {};

    navigate("/student/add-address", {
      state: {
        location: locationToUse,
        editMode: isEditMode,
        addressData: addressData,
        // Pass the route to return to after saving
        from: location.state?.from || "/student/saved-addresses",
        // Pass location details for address creation
        // Priority: geocoded address > existing address data (edit mode) > empty
        locationDetails: {
          lat: locationToUse.lat,
          lng: locationToUse.lng,
          city: addressDetails.city || (isEditMode && addressData?.city) || '',
          state: addressDetails.state || (isEditMode && addressData?.state) || '',
          postalCode: addressDetails.postalCode || (isEditMode && addressData?.postalCode) || '',
          country: addressDetails.country || (isEditMode && addressData?.country) || '',
          formattedAddress: addressDetails.formattedAddress || '',
        }
      }
    });
  };

  return (
    <div
      className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* NAVBAR */}
        <Navbar
          onBack={() => {
            // Navigate back to saved addresses if coming from edit flow, otherwise go back
            const from = location.state?.from;
            if (from) {
              navigate(from);
            } else {
              navigate(-1);
            }
          }}
          background="transparent"
          spacerHeight={40}
        />

        {/* CONTENT */}
        <main className="flex-1 px-5">
          <div className="pt-4">
            <PageTitle>{isEditMode ? "Edit Location" : "Select Location"}</PageTitle>

            {/* Search */}
            <div className="mt-4">
              {mapsLoaded && !mapsError ? (
                <Autocomplete
                  onLoad={(ac) => {
                    autocompleteRef.current = ac;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                >
                  <input
                    type="text"
                    placeholder="Enter Pincode, City or Location"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="w-full h-12 px-4 bg-white border border-[#E1E1E1] text-[#2D2D2D] placeholder-[#8A8A8A] focus:outline-none"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  placeholder="Enter Pincode, City or Location"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="w-full h-12 px-4 bg-white border border-[#E1E1E1] text-[#2D2D2D] placeholder-[#8A8A8A] focus:outline-none"
                />
              )}
            </div>

            {/* Map */}
            <div className="mt-4">
              {mapsError ? (
                <div className="w-full h-[200px] bg-gray-100 border border-gray-300 flex flex-col items-center justify-center p-4">
                  <div className="text-gray-600 text-sm text-center mb-2">
                    Map unavailable
                  </div>
                  <div className="text-gray-500 text-xs text-center">
                    You can still proceed by clicking "ADD ADDRESS" below. Location will be saved using coordinates.
                  </div>
                </div>
              ) : !googleMapsApiKey ? (
                <div className="w-full h-[200px] bg-gray-100 border border-gray-300 flex flex-col items-center justify-center p-4">
                  <div className="text-gray-600 text-sm text-center mb-2">
                    Map unavailable
                  </div>
                  <div className="text-gray-500 text-xs text-center">
                    Google Maps API key is not configured for this environment.
                  </div>
                </div>
              ) : (
                <LoadScript
                  googleMapsApiKey={googleMapsApiKey}
                  libraries={['places', 'geocoding']}
                  onLoad={() => {
                    console.log('Google Maps API loaded');
                    // Small delay to check if maps actually initializes
                    setTimeout(() => {
                      if (typeof window !== 'undefined' && window.google?.maps) {
                        setMapsLoaded(true);
                        setMapsError(null);
                      } else {
                        setMapsError('Map API loaded but not fully initialized');
                        setMapsLoaded(false);
                      }
                    }, 500);
                  }}
                  onError={(error) => {
                    console.error('Error loading Google Maps API:', error);
                    setMapsLoaded(false);
                    setMapsError('Failed to load map. Please ensure billing is enabled for Google Maps API.');
                  }}
                >
                  {(() => {
                    // Don't render map if there's an error
                    if (mapsError) {
                      return null;
                    }

                    // Only render map if fully loaded and available
                    if (mapsLoaded && typeof window !== 'undefined' && window.google?.maps) {
                      try {
                        return (
                          <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={center}
                            zoom={12}
                            onClick={onMapClick}
                            onLoad={() => {
                              setMapsLoaded(true);
                              setMapsError(null);
                            }}
                            options={{
                              fullscreenControl: false,
                              mapTypeControl: false,
                              streetViewControl: false,
                              zoomControl: false,
                              clickableIcons: false,
                            }}
                          >
                            {selectedLocation && window.google?.maps?.Size && window.google?.maps?.Point && (
                              <Marker
                                position={selectedLocation}
                                icon={{
                                  url: pinSvg(),
                                  scaledSize: new window.google.maps.Size(28, 28),
                                  anchor: new window.google.maps.Point(14, 28), // tip on point
                                }}
                              />
                            )}
                          </GoogleMap>
                        );
                      } catch (error) {
                        console.error('Error rendering GoogleMap:', error);
                        setMapsError('Error rendering map');
                        return null;
                      }
                    }

                    // Show loading state
                    return (
                      <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center">
                        <div className="text-gray-500">Loading map...</div>
                      </div>
                    );
                  })()}
                </LoadScript>
              )}
            </div>

            {/* Set current location (link-style) */}
            {!mapsError && (
              <button
                type="button"
                onClick={useCurrentLocation}
                className="mt-5 w-full flex items-center justify-center gap-2 text-[#D62422] font-extrabold uppercase text-[12px]"
              >
                {/* circular crosshair icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M12 6v12M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Set my current location</span>
              </button>
            )}

            {/* CTA */}
            <div className="mt-10 pb-[max(16px,env(safe-area-inset-bottom))]">
              <PrimaryButton
                label={isEditMode ? "UPDATE ADDRESS" : "ADD ADDRESS"}
                onClick={addAddress}
                className="!w-full"
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SetLocation;
